/** Represents a comparison operator in a CQL2 request filter,
 * referencing a property of a CQL2Clause. */
export type CQL2Operator = '=' | 'and' | 'or' | 'in' | 'between' | 's_intersects' | 'isNull';

/** Represents a clause in a CQL2 request filter. */
export type CQL2Clause = {
    /** The logical operator of the clause.
     * (=, in, between, etc.) */
    op: CQL2Operator;

    /** The list of arguments for the logical operator.
     * Note: Automatically formatted by `CQL2Expr.addClause()`, not necessary to manually set.*/
    args: any[];

    /** What property does this reference? */
    property?: string;

    /** What are the values for the logical operator? */
    value?: any[];
};

/** An object that represents a CQL2 expression.
 * Allows for clauses to be added with `addClause` before
 * outputing the result with `json()`. */
export class CQL2Expr {
    private filter: CQL2Clause;

    constructor(op: CQL2Operator = 'and') {
        // The top-level clause that joins all conditions together.
        // This should always be an 'and' comparison.
        this.filter = {
            op,
            args: [],
        };
    }

    /** Adds a clause to this CQL2 object. */
    public addClause(property: string, op: CQL2Operator, ...value: any) {
        const subclause = { op, args: [{ property }, ...value] };
        this.filter.args.push(subclause);
    }

    /** Nest another expression inside this one.  */
    public addExpr(filter: CQL2Expr) {
        const subclause = filter.json();
        this.filter.args.push(subclause);
    }

    /** Returns a JSON object representing the `filter` property of a CQL2 search request */
    public json() {
        return this.filter;
    }

    /** Returns whether or not there are any clauses in the filter */
    public hasFilter() {
        return this.filter.args.length > 0;
    }
}
